"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectItem } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, AlertCircle } from "lucide-react";
import Link from "next/link";
import { clientLogger } from "@/lib/client-logger";

interface IYKChip {
  uid: string;
  createdAt: string;
  isConfigured: boolean;
  name?: string | null;
  description?: string | null;
  localRedirectUrl?: string | null;
  isLocallyActive?: boolean;
}

interface IYKChipGroup {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface IYKChipSelectorProps {
  value: string;
  onChange: (chipId: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

export function IYKChipSelector({ 
  value, 
  onChange, 
  label = "IYK Chip", 
  placeholder = "Select a chip",
  className = ""
}: IYKChipSelectorProps) {
  const [chipGroups, setChipGroups] = useState<IYKChipGroup[]>([]);
  const [chips, setChips] = useState<IYKChip[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasApiKey, setHasApiKey] = useState(false);

  useEffect(() => {
    checkApiKeyAndLoadGroups();
  }, []);

  // Effect to load the chip group and chips when value is provided (for editing)
  useEffect(() => {
    if (value && chipGroups.length > 0 && !selectedGroup) {
      loadChipFromValue(value);
    }
    // Only run when value or chipGroups change, selectedGroup is checked but not a dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, chipGroups]);

  const loadChipFromValue = async (chipId: string) => {
    try {
      setIsLoading(true);
      setError("");
      
      // Fetch the chip details to find its group
      const response = await fetch(`/api/iyk/chips?chipId=${chipId}`);
      if (response.ok) {
        const data = await response.json();
        const chip = data.chip;
        
        if (chip && chip.chipGroupId) {
          // Set the group
          setSelectedGroup(chip.chipGroupId.toString());
          
          // Load all chips in that group (including the selected one)
          const chipsResponse = await fetch(`/api/iyk/chips?chipGroupId=${chip.chipGroupId}&includeAssigned=true`);
          if (chipsResponse.ok) {
            const chipsData = await chipsResponse.json();
            setChips(chipsData.chips || []);
          }
        }
      }
    } catch (error) {
      clientLogger.error("Error loading chip from value", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkApiKeyAndLoadGroups = async () => {
    try {
      setIsLoading(true);
      setError("");

      // Check if API key is configured
      const settingsResponse = await fetch("/api/settings");
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        const apiKeyConfigured = settingsData.tenant?.iykApiKeySet;

        if (!apiKeyConfigured) {
          setHasApiKey(false);
          return;
        }
        
        setHasApiKey(true);

        // Load chip groups
        const groupsResponse = await fetch("/api/iyk/chip-groups");
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setChipGroups(groupsData.chipGroups || []);
        } else {
          const errorData = await groupsResponse.json();
          setError(errorData.error || "Failed to load chip groups");
        }
      }
    } catch (error) {
      clientLogger.error("Error loading IYK data", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      setError("Failed to load IYK configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const loadChips = async (groupId: string) => {
    try {
      setIsLoading(true);
      setError("");

      // Load all chips in the group
      const response = await fetch(`/api/iyk/chips?chipGroupId=${groupId}&includeAssigned=true`);
      if (response.ok) {
        const data = await response.json();
        setChips(data.chips || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load chips");
      }
    } catch (error) {
      clientLogger.error("Error loading chips", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      setError("Failed to load chips");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroupChange = (groupId: string) => {
    setSelectedGroup(groupId);
    setChips([]);
    onChange(""); // Clear selected chip when group changes
    if (groupId) {
      loadChips(groupId);
    }
  };

  const handleChipChange = (chipId: string) => {
    onChange(chipId);
  };

  if (!hasApiKey) {
    return (
      <div className={className}>
        <Label>{label}</Label>
        <Alert className="mt-1">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            IYK API key not configured. 
            <Link href="/dashboard/settings?tab=integrations" className="ml-1 text-blue-600 hover:text-blue-800 underline">
              Configure API Key
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={className}>
      <Label>{label}</Label>
      
      {error && (
        <Alert className="mt-1 mb-2" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="link" 
              size="sm" 
              onClick={checkApiKeyAndLoadGroups}
              className="ml-2 p-0 h-auto"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3 mt-1">
        {/* Chip Group Selection */}
        <div>
          <Label className="text-sm text-gray-600">Chip Group</Label>
          <Select 
            value={selectedGroup} 
            onChange={(e) => handleGroupChange(e.target.value)}
            className="mt-1"
          >
            <SelectItem value="">Select a chip group</SelectItem>
            {chipGroups.map((group) => (
              <SelectItem key={group.id} value={group.id.toString()}>
                {group.name} (ID: {group.id})
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Chip Selection */}
        {selectedGroup && (
          <div>
            <Label className="text-sm text-gray-600">Chip</Label>
            <Select 
              value={value} 
              onChange={(e) => handleChipChange(e.target.value)} 
              disabled={isLoading}
              className="mt-1"
            >
              <SelectItem value="">{isLoading ? "Loading chips..." : placeholder}</SelectItem>
              {chips.map((chip) => (
                <SelectItem key={chip.uid} value={chip.uid}>
                  {chip.name ? `${chip.name} (${chip.uid})` : chip.uid}
                </SelectItem>
              ))}
            </Select>
          </div>
        )}

        {/* Help Text */}
        <div className="text-xs text-gray-500">
          Need help managing chips?
          <Link href="/dashboard/iyk-chips" className="ml-1 text-blue-600 hover:text-blue-800 underline">
            Manage IYK Chips
          </Link>
        </div>
      </div>
    </div>
  );
}
